import "@/styles/components/site-header.css";
import delegate from "delegate-it";
import { fireEvent } from "@/scripts/common/util";
import { gsap } from "gsap";

const siteHeader = document.querySelector(".site-header");
const scrollPixel = document.querySelector(".scroll-pixel");
const siteMenu = document.getElementById("site-menu");

const handleMenuOpen = () => {
  gsap
    .timeline({
      onComplete: () => {
        requestAnimationFrame(() => {
          siteMenu.classList.add("is-open");
        });
      },
    })
    .fromTo(
      siteMenu.querySelectorAll(".site-menu-actions .btn, .site-menu-actions button"),
      { opacity: 0, yPercent: 20 },
      { opacity: 1, yPercent: 0, stagger: 0.2, ease: "ease-in" },
    )
    .fromTo(
      siteMenu.querySelectorAll(".menu-primary > li"),
      { opacity: 0, yPercent: 20 },
      { opacity: 1, yPercent: 0, stagger: 0.2, ease: "ease-in" },
      "<",
    )
    .fromTo(
      siteMenu.querySelectorAll(".menu-secondary > li"),
      { opacity: 0, yPercent: 10 },
      { opacity: 1, yPercent: 0, stagger: 0.2, ease: "ease-in" },
      "<50%",
    )
    .fromTo(
      siteMenu.querySelectorAll(".menu-social > li"),
      { opacity: 0, yPercent: 10 },
      { opacity: 1, yPercent: 0, stagger: 0.2, ease: "ease-in" },
      "<",
    )
    .duration(1);
};

const handleMenuClose = () => {
  gsap
    .timeline({
      onComplete: () => {
        requestAnimationFrame(() => {
          siteMenu.classList.remove("is-open");
        });
      },
    })
    .to(siteMenu.querySelectorAll(".site-menu-actions .btn, .site-menu-actions button"), {
      opacity: 0,
      yPercent: -20,
      stagger: 0.2,
      ease: "ease-in",
    })
    .to(
      siteMenu.querySelectorAll(".menu-primary > li"),
      { opacity: 0, yPercent: -20, stagger: 0.2, ease: "ease-in" },
      "<",
    )
    .to(
      siteMenu.querySelectorAll(".menu-secondary > li"),
      { opacity: 0, yPercent: -10, stagger: 0.2, ease: "ease-in" },
      "<",
    )
    .to(
      siteMenu.querySelectorAll(".menu-social > li"),
      { opacity: 0, yPercent: -10, stagger: 0.2, ease: "ease-in" },
      "<",
    )
    .duration(0.5);

  fireEvent("slidetoggle:reset", siteMenu.querySelectorAll('[data-slide-toggle][aria-expanded="true"]'));
};

const setupSubmenus = () => {
  const siteHeaderNav = siteHeader.querySelector(".site-header-nav");
  const wrapper = siteHeaderNav.querySelector(".site-header-submenus-wrapper");
  const content = siteHeaderNav.querySelector(".site-header-submenus");
  const navItems = siteHeaderNav.querySelectorAll(".nav-item[aria-haspopup]");
  const background = siteHeaderNav.querySelector(".site-header-submenu-background");
  const arrow = siteHeaderNav.querySelector(".site-header-submenu-arrow");
  const dimensions = {};
  let dimensionsSet = false;
  let largestDimension;

  const setDimensions = () => {
    const wrapperLeft = wrapper.getBoundingClientRect().x;
    const siteHeaderNavRect = siteHeaderNav.getBoundingClientRect();

    navItems.forEach((item) => {
      const itemRect = item.getBoundingClientRect();
      const target = document.getElementById(item.getAttribute("aria-controls"));
      const targetRect = target.getBoundingClientRect();

      let x = itemRect.x + itemRect.width / 2 - targetRect.width / 2;

      if (targetRect.width > siteHeaderNavRect.width) {
        if (targetRect.width + itemRect.x > window.innerWidth) {
          x = window.innerWidth / 2 - targetRect.width / 2;
        } else {
          x = itemRect.x - itemRect.width;
        }
      }

      dimensions[target.id] = {
        width: targetRect.width,
        height: targetRect.height,
        x: x - wrapperLeft,
        arrowX: itemRect.left + itemRect.width / 2 - wrapperLeft,
      };
    });

    largestDimension = Object.values(dimensions).reduce(
      (max, value) => {
        return value.width > max.width ? value : max;
      },
      { width: 0 },
    );

    dimensionsSet = true;
  };

  const hideSubmenus = () => {
    siteHeaderNav.querySelectorAll('.nav-item[aria-expanded="true"]').forEach((el) => {
      el.setAttribute("aria-expanded", "false");
    });

    siteHeaderNav.querySelectorAll('.site-header-submenu[aria-hidden="false"]').forEach((el) => {
      el.setAttribute("aria-hidden", "true");
    });
  };

  const showSubmenu = (navItem, target) => {
    const targetDimensions = dimensions[target.id];

    hideSubmenus();

    navItem.setAttribute("aria-expanded", "true");
    target.setAttribute("aria-hidden", "false");

    arrow.style.transform = `
      translateX(${targetDimensions.arrowX}px)
      rotate(45deg)
    `;

    background.style.width = `${largestDimension.width}px`;
    background.style.height = `${largestDimension.height}px`;
    background.style.transform = `
      translate3d(${targetDimensions.x}px, 0px, 0px)
      scale3d(${targetDimensions.width / largestDimension.width}, 1, 1)
      scale3d(1, ${targetDimensions.height / largestDimension.height}, 1)
    `;

    content.style.width = targetDimensions.width + "px";
    content.style.height = targetDimensions.height + "px";
    content.style.transform = `translate3d(${targetDimensions.x}px, 0px, 0px)`;

    wrapper.classList.add("is-active");
  };

  navItems.forEach((item) => {
    item.addEventListener("mouseenter", () => {
      const target = document.getElementById(item.getAttribute("aria-controls"));

      if (dimensionsSet) {
        showSubmenu(item, target);
      }
    });
  });

  siteHeaderNav.querySelectorAll(".nav-item:not([aria-haspopup])").forEach((item) => {
    item.addEventListener("mouseenter", () => {
      wrapper.classList.remove("is-active");

      hideSubmenus();
    });
  });

  siteHeaderNav.addEventListener("mouseleave", () => {
    wrapper.classList.remove("is-active");

    hideSubmenus();
  });

  requestAnimationFrame(setDimensions);

  const observer = new ResizeObserver(setDimensions);

  observer.observe(siteHeaderNav);
};

export default {
  init: () => {
    if (siteHeader && scrollPixel) {
      const observer = new IntersectionObserver(
        ([e]) => siteHeader.classList.toggle("is-stuck", e.intersectionRatio < 1),
        {
          threshold: [1],
        },
      );

      observer.observe(document.querySelector(".scroll-pixel"));
    }

    delegate("html", "sitemenu:open", handleMenuOpen);
    delegate("html", "sitemenu:close", handleMenuClose);

    setupSubmenus();
  },
};
